import { Button, Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import React, { useMemo } from 'react'
import { channelById } from '../../../constants/Channels'
import ExternalChannelLink from '../../ExternalChannelLink'
import GridSquare from '../GridSquare'
import { OrderDetails } from '../orders/OrderDetails'
import NiceListingStatus from '../../NiceListingStatus'
import ActionBar from './ActionBar'
import Header from './Header'
import ItemDetails from './ItemDetails'
import ListingStrategyRunDetails from './ListingStrategyRunDetails'
import { ListingSequence } from './ListingSequence'
import ProductDetails from './ProductDetails'
import ProductListings from './ProductListings'
import { InventoryUnitsDetails } from '../InventoryUnitsDetails'
import { VariantDetails } from '../VariantDetails'

const Renderer = ({
  listing,
  product,
  productListings,
  channelId,
  children,
  onClose,
  onProductListingClick,
  onRelist,
  onDelist,
  onRecycle,
  onDelete,
  onList,
  onListingStrategyChange
}) => {
  const channel = channelById(channelId)
  const isChannelLinkVisible = useMemo(() => {
    if (channel.name === 'amazon') {
      return listing.asin
    }

    return listing.externalMarketId
  }, [channel, listing])

  return (
    <div className="product-drawer">
      <div className="close">
        <Button minimal onClick={onClose}>
          <Icon icon={IconNames.CROSS} size={25} />
        </Button>
      </div>
      <Header listing={listing} product={product} />
      <ProductDetails product={product} />
      {product.unitTracking ? (
        <InventoryUnitsDetails
          inventoryUnits={product.inventoryUnits?.map(u => ({ ...u, isMedia: product.isMedia }))}
        />
      ) : (
        <VariantDetails product={product} />
      )}

      <OrderDetails product={product} />

      <ItemDetails>
        <GridSquare
          title="Channel"
          contents={listing.marketAccount ? listing.marketAccount.fullHumanReadableName : channel.humanReadableName}
        />

        <GridSquare
          title="Channel ID"
          contents={
            isChannelLinkVisible && (
              <div style={{ display: 'inline-block' }}>
                <ExternalChannelLink
                  channel={channel.name}
                  id={listing.externalMarketId}
                  asin={listing.asin}
                  listingUrl={listing.listingUrl}
                />
              </div>
            )
          }
        />
        <GridSquare
          title="Status"
          contents={
            <NiceListingStatus
              value={listing.state}
              errors={listing[channel.errorKey]}
              message={channel.name === 'ebay' ? listing.ebayMessage : null}
            />
          }
        />

        {children}
        <GridSquare
          title="Quantity"
          contents={
            product.unitTracking
              ? (product.inventoryUnits?.filter(u => u.warehouseStatus === 'available').length ?? 0)
              : (product.variants?.reduce((sum, v) => sum + (v.quantity || 0), 0) ?? 0)
          }
        />
        <GridSquare title="" contents=" " />
      </ItemDetails>
      {product.isMedia && product.isRepricingPaused === false ? <ListingSequence /> : null}
      {product.listingStrategyRun && product.isRepricingPaused ? (
        <ListingStrategyRunDetails
          listingStrategyRun={product.listingStrategyRun}
          listingStrategyId={product.listingStrategyId}
          productId={product.id}
          onChange={onListingStrategyChange}
          overridesActive={!!product.overriddenValues}
        />
      ) : null}
      <ProductListings
        productListings={productListings}
        currentListingId={listing.id}
        channelId={channelId}
        onCardClick={onProductListingClick}
      />
      <ActionBar
        product={product}
        listing={listing}
        channelId={channelId}
        onList={onList}
        onDelist={onDelist}
        onRelist={onRelist}
        onRecycle={onRecycle}
        onDelete={onDelete}
      />
    </div>
  )
}

export default Renderer
